import React, { memo, useCallback, useMemo, useState } from "react";

import CheckBox from "components/common/checkBox/checkbox";
import moment from "moment";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import {
  baseURLImage,
  capitalizeWords,
  formatAmount,
  getNameProfile,
  parseBooleanVal,
  timeToQuantity,
} from "Utils/utils";
import { getShiftListBillingStatus, getShiftListBillingStatusClass } from "../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import RosterInvoiceBreakdownPanel, {
  resolveCreateInvoiceDisplayedShiftTotal,
} from "./RosterInvoiceBreakdownPanel";
import TimesheetStatusCell from "../components/TimesheetStatusCell";

const isAdvanceShift = (item) =>
  Boolean(
    item?.billing_mode === "advance" ||
      item?.is_v5_multi_assignment === true ||
      item?.is_multi_assignment === true
  );

const isSplitShift = (item) =>
  !isAdvanceShift(item) &&
  Boolean(item?.is_split_shift || item?.billing_mode === "split");

const resolveServiceType = (item) => {
  if (isAdvanceShift(item)) {
    return "Advance shift";
  }
  if (item?.is_split_shift || item?.billing_mode === "split") {
    return "This is split shift";
  }
  return item?.invoice_service_type ?? "—";
};

const isStaShift = (item) => {
  const value = item?.is_sta ?? item?.sta;
  return parseBooleanVal(value) || value === 1 || String(value) === "1";
};

const resolveInvoiceListWorkers = (item) => {
  const raw =
    Array.isArray(item?.workers) && item.workers.length > 0
      ? item.workers
      : item?.employee_profile
        ? [item.employee_profile]
        : [];

  const seen = new Set();
  return raw.filter((worker) => {
    const key =
      worker?.worker_id ??
      worker?.id ??
      `${worker?.first_name || ""}|${worker?.last_name || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const RosterInvoiceTable = ({
  item,
  isChecked,
  onCheckboxChange,
  showClaimTypeScheduleTimesheets = true,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const shiftTotal = useMemo(
    () => resolveCreateInvoiceDisplayedShiftTotal(item),
    [item]
  );
  const billingStatus = useMemo(() => getShiftListBillingStatus(item), [item]);
  const workers = useMemo(() => resolveInvoiceListWorkers(item), [item]);
  const primaryWorker = workers[0] || {};
  const serviceType = useMemo(() => resolveServiceType(item), [item]);
  const advanceShift = useMemo(() => isAdvanceShift(item), [item]);
  const splitShift = useMemo(() => isSplitShift(item), [item]);
  const staShift = useMemo(() => isStaShift(item), [item]);

  const handleRowClick = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleCheckboxChange = useCallback(
    (checked) => {
      onCheckboxChange(checked, item.uuid, item);
    },
    [onCheckboxChange, item]
  );

  // getNameProfile returns "" unless both names are set, and an empty src would
  // re-fire onError against the page URL, so fall back to placeholder initials.
  const avatarFallback = useMemo(
    () =>
      getNameProfile(
        primaryWorker.first_name || "N",
        primaryWorker.last_name || "A"
      ),
    [primaryWorker.first_name, primaryWorker.last_name]
  );

  const handleImageError = useCallback(
    (e) => {
      e.target.onerror = null;
      e.target.src = avatarFallback;
    },
    [avatarFallback]
  );

  const formattedDate = useMemo(
    () => moment(item?.time_sheet_date).format("ddd, D MMM YY"),
    [item?.time_sheet_date]
  );

  const timesheetQuantity = useMemo(
    () => timeToQuantity(item?.timesheet_length),
    [item?.timesheet_length]
  );

  const formattedShiftTotal = useMemo(
    () => (shiftTotal != null ? `$${formatAmount(shiftTotal)}` : "N/A"),
    [shiftTotal]
  );

  const singleParticipantLabel = useMemo(() => {
    if (item?.participants?.length !== 1) return null;
    const participant = item.participants[0];
    return `${capitalizeWords(participant?.first_name)} ${capitalizeWords(participant?.last_name)}`;
  }, [item?.participants]);

  return (
    <>
      <tr
        className={`text-center transition-colors ${
          isChecked ? "bg-[#2D4575] text-white" : "even:bg-white odd:bg-gray-50"
        }`}
      >
        <td className="whitespace-nowrap pl-2 pr-1 py-4 text-sm">
          <CheckBox handleChange={handleCheckboxChange} checked={isChecked} />
        </td>
        <td className="whitespace-nowrap pl-2 pr-1 py-4 text-sm" onClick={handleRowClick}>
          <button
            type="button"
            className={`flex cursor-pointer items-center rounded-md p-0.5 transition-colors ${
              isChecked ? "text-white hover:bg-white/10" : "text-gray-500 hover:bg-gray-100"
            }`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse breakdown" : "Expand breakdown"}
          >
            {isExpanded ? <BiChevronUp size={22} /> : <BiChevronDown size={22} />}
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-start">
          <div className="max-w-[150px] truncate">
            {singleParticipantLabel ? (
              <span className="capitalize" title={singleParticipantLabel.trim()}>
                {singleParticipantLabel}
              </span>
            ) : (
              <AvatarGroup
                arrayData={item?.participants}
                cssClass="mt-0"
                maxLength={10}
                isNoRing={true}
              />
            )}
          </div>
        </td>
        <td className="px-3 py-4 text-sm text-start capitalize">
          {workers.length > 1 ? (
            <div className="max-w-[180px]">
              <AvatarGroup
                arrayData={workers}
                cssClass="mt-0"
                maxLength={10}
                isNoRing={true}
                type="worker"
              />
            </div>
          ) : workers.length === 1 ? (
            <div className="flex max-w-[180px] items-center">
              <div className="h-10 w-10 flex-shrink-0">
                <img
                  onError={handleImageError}
                  className="h-10 w-10 rounded-full object-cover capitalize ring-2 ring-white"
                  src={
                    primaryWorker.photo
                      ? baseURLImage(primaryWorker.photo)
                      : avatarFallback
                  }
                  alt={`${primaryWorker.first_name || ""} ${primaryWorker.last_name || ""}`.trim()}
                />
              </div>
              <div
                className="ml-2 min-w-0 flex-1 truncate font-medium"
                title={`${primaryWorker.first_name || ""} ${primaryWorker.last_name || ""}`.trim()}
              >
                {primaryWorker.first_name} {primaryWorker.last_name}
              </div>
            </div>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <TimesheetStatusCell
          item={item}
          tdClassName="whitespace-nowrap px-3 py-4 text-sm text-center"
        />
        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">{formattedDate}</td>
        <td className="px-3 py-4 text-sm text-center">
          <div className="inline-flex max-w-[220px] flex-wrap items-center justify-center gap-1">
            {advanceShift ? (
              <span
                className="inline-flex max-w-[180px] truncate rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-800"
                title="Advance shift"
              >
                Advance
              </span>
            ) : splitShift ? (
              <span
                className="inline-flex max-w-[180px] truncate rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-800"
                title={serviceType}
              >
                Split shift
              </span>
            ) : serviceType !== "—" ? (
              <span
                className="inline-block max-w-[180px] truncate rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800"
                title={serviceType}
              >
                {serviceType}
              </span>
            ) : !staShift ? (
              "—"
            ) : null}
            {staShift && (
              <span
                className="inline-flex shrink-0 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-800"
                title="Short Term Accommodation (STA) shift"
              >
                STA
              </span>
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${getShiftListBillingStatusClass(
              billingStatus.tone
            )}`}
          >
            {billingStatus.label}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-center font-medium">
          {timesheetQuantity}
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-center font-semibold">
          {formattedShiftTotal}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={10} className="bg-[#eef2f7] px-3 py-4 sm:px-5">
            <RosterInvoiceBreakdownPanel
              item={item}
              showClaimTypeScheduleTimesheets={showClaimTypeScheduleTimesheets}
              shiftTotal={shiftTotal}
            />
          </td>
        </tr>
      )}
    </>
  );
};

export default memo(RosterInvoiceTable);
